import { useMemo } from "react";
import { classifyItemStorage, STORAGE } from "../../../utils/storageClassifier";

const VALID_STORAGE = new Set([STORAGE.FRIDGE, STORAGE.FREEZER, STORAGE.PANTRY]);

export default function useStorageGroups({
  groupedProducts,
  t,
  getFallbackValue,
  getProductImage,
}) {
  return useMemo(() => {
    const groups = {
      fridge: { label: t("grocery.storage.fridge"), icon: "🧊", items: [] },
      freezer: { label: t("grocery.storage.freezer"), icon: "❄️", items: [] },
      pantry: { label: t("grocery.storage.pantry"), icon: "🥫", items: [] },
    };

    for (const cat of groupedProducts) {
      for (const item of cat.items) {
        if (item.unmatched || item.error) continue;

        // Prefer build-time storage data; fall back to the deterministic
        // classifier for saved / legacy plans that lack it (no missing badges).
        let storageType = (item.storage || "").toLowerCase();
        let shelfLife = item.shelfLifeDays;
        const needsStorage = !VALID_STORAGE.has(storageType);
        const needsShelfLife = !(shelfLife > 0);
        if (needsStorage || needsShelfLife) {
          const classified = classifyItemStorage(
            { ...item, category: cat.category },
            {
              recipeStorageHint: item.storage,
              // When the location is already valid (only the shelf life is missing),
              // trust it so we don't pair e.g. a freezer item with a fridge day count.
              knownStorage: needsStorage ? undefined : storageType,
            }
          );
          if (needsStorage) storageType = classified.storage;
          if (needsShelfLife) shelfLife = classified.shelfLifeDays;
        }

        const entry = {
          productName: getFallbackValue(item.productName, "Unknown Product"),
          lang: item.lang || item.product?.lang || null,
          product: item.product || null,
          category: cat.category,
          shelfLifeDays: shelfLife,
          image: getProductImage(item),
          originalName: item.originalName || item.name,
        };

        if (storageType === "freezer") {
          groups.freezer.items.push(entry);
        } else if (storageType === "pantry") {
          groups.pantry.items.push(entry);
        } else {
          groups.fridge.items.push(entry);
        }
      }
    }

    for (const key of Object.keys(groups)) {
      groups[key].items.sort(
        (a, b) =>
          (a.shelfLifeDays > 0 ? a.shelfLifeDays : 999) -
          (b.shelfLifeDays > 0 ? b.shelfLifeDays : 999)
      );
    }

    return groups;
  }, [groupedProducts, t, getFallbackValue, getProductImage]);
}
